import { useRef, useState } from "react";
import { SiteLayout } from "@/components/SiteLayout";
import { useSite } from "@/hooks/use-site";
import { localizeUrl } from "@/lib/site";
import { pickLocalized } from "@/lib/localized";
import {
  archiveModels,
  SPEC_LABELS,
  type ArchiveModel,
  type ArchiveSpecKey,
} from "@/data/archive-models";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { ChevronRight } from "lucide-react";

export function ArchivePage() {
  const { messages, site, href } = useSite();
  const locale = site.locale;
  const home = localizeUrl("/", site.locale, href);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<ArchiveModel | null>(null);
  const closeTimer = useRef<ReturnType<typeof window.setTimeout> | undefined>(undefined);

  const openModel = (model: ArchiveModel) => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setSelected(model);
    setOpen(true);
  };

  const handleDialogOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) {
      // Keep modal content mounted until Radix close animation finishes (duration-200).
      closeTimer.current = setTimeout(() => setSelected(null), 200);
    }
  };

  const shortDesc = (model: ArchiveModel) => pickLocalized(model, locale, "shortDesc");
  const title = (model: ArchiveModel) => pickLocalized(model, locale, "title");
  const specLabel = (key: ArchiveSpecKey) => SPEC_LABELS[key][locale];

  return (
    <SiteLayout>
      {/* Breadcrumb */}
      <div className="border-b border-border/60 bg-background/60">
        <nav
          aria-label={messages.breadcrumbLabel}
          className="mx-auto flex max-w-7xl items-center gap-1.5 px-6 py-3 text-xs text-muted-foreground"
        >
          <a href={home} className="hover:text-foreground">
            {messages.tv6UltraBreadcrumbHome}
          </a>
          <ChevronRight aria-hidden className="h-3 w-3" />
          <a href={localizeUrl("/products", site.locale, href)} className="hover:text-foreground">
            {messages.tv6UltraBreadcrumbProducts}
          </a>
          <ChevronRight aria-hidden className="h-3 w-3" />
          <span className="text-foreground">{messages.archiveTitle}</span>
        </nav>
      </div>

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-border/60 bg-muted/10">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 -z-10 bg-grid opacity-20"
        />
        <div className="mx-auto max-w-3xl px-6 py-16 text-center lg:py-24">
          <p className="text-sm font-medium text-primary animate-fade-up">
            {messages.archiveHeroEyebrow}
          </p>
          <h1 className="mt-3 text-4xl font-semibold leading-[1.05] tracking-tight text-foreground sm:text-5xl animate-fade-up">
            {messages.archiveTitle}
          </h1>
          <p className="mx-auto mt-5 max-w-2xl text-lg text-muted-foreground animate-fade-up">
            {messages.archiveSubtitle}
          </p>
        </div>
      </section>

      {/* Grid */}
      <section className="mx-auto max-w-7xl px-6 py-12 lg:py-16">
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 sm:gap-6 lg:grid-cols-3">
          {archiveModels.map((model, idx) => (
            <button
              key={model.id}
              type="button"
              onClick={() => openModel(model)}
              className="group flex animate-fade-up cursor-pointer flex-col overflow-hidden rounded-3xl border border-border/60 bg-card text-left shadow-[var(--shadow-soft)] transition-all duration-300 hover:-translate-y-1 hover:border-primary/30 hover:shadow-[var(--shadow-elevated)]"
              style={{ animationDelay: `${idx * 40}ms` }}
            >
              {/* Image tile — solid light surface that matches the studio photo background */}
              <div className="relative aspect-[4/3] overflow-hidden bg-[#f5f5f5]">
                <img
                  src={model.image}
                  alt={title(model)}
                  className="absolute inset-0 h-full w-full object-contain p-6 transition-transform duration-500 group-hover:scale-[1.04]"
                  loading="lazy"
                />
                <span className="absolute left-4 top-4 rounded-full bg-white/85 px-3 py-1 text-[11px] font-medium text-slate-600 shadow-sm backdrop-blur">
                  {model.year}
                </span>
              </div>

              {/* Content */}
              <div className="flex flex-1 flex-col p-5 sm:p-6">
                <h3 className="text-base font-semibold leading-snug tracking-tight text-foreground transition-colors group-hover:text-primary sm:text-lg">
                  {title(model)}
                </h3>
                <p className="mt-3 line-clamp-2 text-sm leading-relaxed text-muted-foreground">
                  {shortDesc(model)}
                </p>
                <span className="mt-auto inline-flex items-center gap-1.5 pt-5 text-sm font-semibold text-primary">
                  {messages.archiveViewSpecs}
                  <ChevronRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </span>
              </div>
            </button>
          ))}
        </div>
      </section>

      {/* Detail modal */}
      <Dialog open={open} onOpenChange={handleDialogOpenChange}>
        <DialogContent className="gap-0 overflow-hidden p-0 sm:max-w-lg lg:max-w-5xl xl:max-w-6xl">
          {selected && (
            <div className="grid max-h-[min(90vh,760px)] grid-cols-1 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
              <div className="relative flex items-center justify-center border-b border-border/60 bg-[#f5f5f5] p-6 sm:p-8 lg:min-h-0 lg:border-b-0 lg:border-r lg:p-10">
                <img
                  src={selected.image}
                  alt={title(selected)}
                  className="max-h-[min(36vh,260px)] w-full object-contain lg:max-h-full lg:min-h-[240px]"
                />
                <span className="absolute left-4 top-4 rounded-full bg-white/85 px-3 py-1 text-[11px] font-medium text-slate-600 shadow-sm backdrop-blur sm:left-5 sm:top-5">
                  {selected.year}
                </span>
              </div>

              <div className="min-h-0 overflow-y-auto p-6 sm:p-8">
                <DialogHeader className="space-y-2 text-left">
                  <span className="inline-flex w-fit rounded-full bg-primary/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-foreground/90">
                    {messages.archiveBadge}
                  </span>
                  <DialogTitle className="text-xl font-bold tracking-tight text-foreground sm:text-2xl">
                    {title(selected)}
                  </DialogTitle>
                  <DialogDescription className="text-sm leading-relaxed text-muted-foreground">
                    {shortDesc(selected)}
                  </DialogDescription>
                </DialogHeader>

                <h3 className="mb-3 mt-6 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {messages.archiveSpecsHeading}
                </h3>
                <dl className="overflow-hidden rounded-2xl border border-border/60 [&>div+div]:border-t [&>div+div]:border-border/50">
                  {(Object.keys(selected.specs) as ArchiveSpecKey[]).map((key) => (
                    <div
                      key={key}
                      className="grid grid-cols-[40%_60%] gap-3 px-4 py-3 odd:bg-muted/30 sm:px-5 sm:py-3.5"
                    >
                      <dt className="text-sm font-medium text-muted-foreground">
                        {specLabel(key)}
                      </dt>
                      <dd className="text-sm font-medium text-foreground">{selected.specs[key]}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </SiteLayout>
  );
}
